import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';

@Injectable({ providedIn: 'root' })
export class QuizService {
  #http = inject(HttpClient);

  private readonly locale = 'en';

  /**
   * Fetches questions for a quiz
   */
  getQuestions(quizId: string): Promise<ChallengeGen.Question[]> {
    return lastValueFrom(
      this.#http
        .get<ChallengeGen.Question[]>(
          `${environment.baseUrl}/tasks/quiz/questions`,
          {
            params: { quizId, locale: this.locale },
          },
        )
        .pipe(take(1)),
    );
  }

  /**
   * Notifies backend that user has started the task (required before completeTask).
   * Creates a pending task to track the attempt.
   */
  startTask(taskId: string): Promise<void> {
    return lastValueFrom(
      this.#http
        .post<void>(`${environment.baseUrl}/tasks/start`, null, {
          params: { id: taskId },
        })
        .pipe(take(1)),
    );
  }

  /**
   * Marks the task as completed (legacy). Use completeTaskWithDetails for quiz tasks.
   * @deprecated Use completeTaskWithDetails for quiz tasks to pass accuracy data
   */
  completeTask(taskId: string): Promise<void> {
    return lastValueFrom(
      this.#http
        .patch<void>(`${environment.baseUrl}/tasks/complete`, null, {
          params: { taskId },
        })
        .pipe(take(1)),
    );
  }

  /**
   * Marks the task as completed with quiz accuracy data.
   * Applies accuracy factor and perfect-quiz bonus to shield calculation.
   */
  completeTaskWithDetails(
    taskId: string,
    completionData: {
      quizCorrectAnswers: number;
      quizTotalQuestions: number;
      isPerfectQuiz: boolean;
    },
  ): Promise<unknown> {
    return lastValueFrom(
      this.#http
        .patch(`${environment.baseUrl}/tasks/complete-with-details`, {
          taskId,
          completionData,
        })
        .pipe(take(1)),
    );
  }

  /**
   * Checks if the selected answer is correct
   */
  checkAnswer(
    questionId: string,
    answerId: string,
  ): Promise<ChallengeGen.Answer & { isCorrect?: boolean }> {
    return lastValueFrom(
      this.#http
        .get<ChallengeGen.Answer & { isCorrect?: boolean }>(
          `${environment.baseUrl}/tasks/quiz/answer`,
          {
            params: {
              questionId,
              answerId,
              locale: this.locale,
            },
          },
        )
        .pipe(take(1)),
    );
  }
}
